/**
 * Single node in the folder tree.
 * Manages its own expand/collapse state via the Zustand store.
 * Shows a right-click / kebab context menu for mutations.
 * Supports:
 *  - Drop target for test case drag-and-drop
 *  - Drag handle to move folders into other folders
 *  - Count badge showing the number of test cases directly in this folder
 */

import { useState, useCallback } from 'react';
import { ChevronRight, Folder, FolderOpen, MoreHorizontal, GripVertical } from 'lucide-react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { useTestCasesStore } from '../../../store/useTestCasesStore';
import { FolderTreeNodeMenu } from './FolderTreeNodeMenu';
import type { IFolderNode, ITestFolder } from '../types';

interface FolderTreeNodeProps {
    node: IFolderNode;
    allFolders: ITestFolder[];
    folderCountMap?: Map<string, number>;
    onCreateSubfolder: (parentId: string | undefined, name: string) => Promise<void>;
    onRename:          (id: string, name: string) => Promise<void>;
    onDelete:          (id: string, strategy: 'move' | 'delete', targetFolderId?: string) => Promise<void>;
}

export function FolderTreeNode({
    node,
    allFolders,
    folderCountMap,
    onCreateSubfolder,
    onRename,
    onDelete,
}: FolderTreeNodeProps) {
    const selectedFolderId   = useTestCasesStore((s) => s.selectedFolderId);
    const setSelectedFolder  = useTestCasesStore((s) => s.setSelectedFolderId);
    const expandedFolderIds  = useTestCasesStore((s) => s.expandedFolderIds);
    const toggleExpanded     = useTestCasesStore((s) => s.toggleFolderExpanded);

    const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);

    const isExpanded = expandedFolderIds.has(node._id);
    const isSelected = selectedFolderId === node._id;
    const hasChildren = node.children.length > 0;
    const count = folderCountMap?.get(node._id) ?? 0;

    const indent = node.depth * 12;

    // Drop target — accepts dragged test case rows and folder drag.
    // data.type = 'folder' lets DndProvider distinguish this from a test-case-row drop.
    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id:   node._id,
        data: { type: 'folder' },
    });

    // Drag source — lets this folder be dragged into another folder
    const {
        attributes: dragAttributes,
        listeners: dragListeners,
        setNodeRef: setDragRef,
        isDragging,
    } = useDraggable({
        id:   `folder:${node._id}`,
        data: { label: node.name, type: 'folder' },
    });

    // Merge the droppable and draggable refs onto the same row element
    const mergedRef = useCallback((el: HTMLElement | null) => {
        setDropRef(el);
        setDragRef(el);
    }, [setDropRef, setDragRef]);

    const handleSelect = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedFolder(node._id);
        if (hasChildren) toggleExpanded(node._id);
    }, [node._id, hasChildren, setSelectedFolder, toggleExpanded]);

    const handleChevron = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        toggleExpanded(node._id);
    }, [node._id, toggleExpanded]);

    const handleContextMenu = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setMenu({ x: e.clientX, y: e.clientY });
    }, []);

    const handleKebab = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setMenu({ x: rect.right, y: rect.bottom });
    }, []);

    // ArrowRight: expand if collapsed; ArrowLeft: collapse if expanded
    const handleNodeKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            if (hasChildren && !isExpanded) toggleExpanded(node._id);
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            if (isExpanded) toggleExpanded(node._id);
        }
        // ArrowUp/Down are handled at the FolderTree level
    }, [hasChildren, isExpanded, node._id, toggleExpanded]);

    return (
        <div style={{ opacity: isDragging ? 0.4 : 1 }}>
            {/* Node row */}
            <div
                ref={mergedRef}
                data-testid={`folder-tree-node-${node._id}`}
                role="treeitem"
                aria-expanded={hasChildren ? isExpanded : undefined}
                aria-selected={isSelected}
                tabIndex={isSelected ? 0 : -1}
                className={`group flex items-center rounded-lg cursor-pointer select-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                    ${isOver
                        ? 'bg-blue-200 dark:bg-blue-800/60 ring-1 ring-blue-400'
                        : isSelected
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50'
                    }`}
                style={{ paddingLeft: `${4 + indent}px`, paddingRight: '4px' }}
                onClick={handleSelect}
                onKeyDown={handleNodeKeyDown}
                onContextMenu={handleContextMenu}
            >
                {/* Folder drag handle — visible on hover, stops row click */}
                <span
                    {...dragAttributes}
                    {...dragListeners}
                    aria-label="Drag to move folder"
                    onClick={(e) => e.stopPropagation()}
                    className="opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 mr-0.5"
                    title="Drag to move folder"
                >
                    <GripVertical size={11} />
                </span>

                {/* Chevron */}
                <span
                    className={`w-4 h-4 flex items-center justify-center flex-shrink-0 transition-transform
                        ${hasChildren ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                        ${isExpanded ? 'rotate-90' : ''}`}
                    onClick={handleChevron}
                >
                    <ChevronRight size={12} />
                </span>

                {/* Folder icon */}
                <span className="mr-1.5 flex-shrink-0">
                    {isExpanded
                        ? <FolderOpen size={14} className={isSelected ? 'text-blue-500' : 'text-slate-400'} />
                        : <Folder size={14} className={isSelected ? 'text-blue-500' : 'text-slate-400'} />
                    }
                </span>

                {/* Name */}
                <span className="flex-1 text-xs font-medium truncate py-1.5 pr-1">{node.name}</span>

                {/* Count badge */}
                {count > 0 && (
                    <span className="ml-auto mr-1 flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400">
                        {count}
                    </span>
                )}

                {/* Kebab — visible on hover */}
                <button
                    data-testid={`folder-node-kebab-${node._id}`}
                    aria-label="Folder options"
                    onClick={handleKebab}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 flex-shrink-0"
                    title="Folder options"
                >
                    <MoreHorizontal size={13} />
                </button>
            </div>

            {/* Children */}
            {isExpanded && hasChildren && (
                <div>
                    {node.children.map((child) => (
                        <FolderTreeNode
                            key={child._id}
                            node={child}
                            allFolders={allFolders}
                            folderCountMap={folderCountMap}
                            onCreateSubfolder={onCreateSubfolder}
                            onRename={onRename}
                            onDelete={onDelete}
                        />
                    ))}
                </div>
            )}

            {/* Context menu portal */}
            {menu && (
                <FolderTreeNodeMenu
                    folder={node}
                    position={menu}
                    onClose={() => setMenu(null)}
                    onCreateSubfolder={onCreateSubfolder}
                    onRename={onRename}
                    onDelete={onDelete}
                    allFolders={allFolders}
                />
            )}
        </div>
    );
}
